/* =============================================================
   StudentExperiences.jsx — Past student experiences
   -------------------------------------------------------------
   Displays experience reports from students who previously went
   on exchange. Filterable by university, year, and rating.
   Helps the current student prepare for their exchange.

   TODO (backend integration):
   - Replace static experiences array with Supabase query
   ============================================================= */

import { useState } from 'react';
import StudentLayout    from '../../components/StudentLayout';
import WorkInProgress  from '../../components/WorkInProgress';

/* ─── Experience data ────────────────────────────────── */

const experiences = [
  {
    id: 1,
    student: 'Laura Fischer',
    year: 'Spring 2025',
    program: 'Master in Management',
    university: 'Imperial College London',
    country: '🇬🇧 UK',
    tags: ['Career boost', 'London life', 'Strong network'],
    avatarColor: 'bg-violet-500',
    initials: 'LF',
    preview:
      'An incredibly enriching semester. The London startup ecosystem opened doors I never expected, and the connections I made will last a lifetime. Make the most of every opportunity the city offers.',
    fullReport: {
      highlights: [
        'World-class faculty with deep industry connections — guest lecturers from top firms every week',
        'London gives you access to one of the best business ecosystems in the world',
        'The international student community is vibrant and welcoming from day one',
      ],
      courses: ['Strategic Innovation', 'Corporate Finance for Managers', 'Entrepreneurship & Venture Capital', 'Global Business Environment'],
      courseTips:
        'Strategic Innovation was the absolute highlight — real case studies with London-based firms. Sign up early as the most popular courses fill fast. Go to every guest lecture you can, the speakers bring incredible real-world perspective.',
      socialLife:
        'The International Student Society organises weekly events that make it easy to meet people from 25+ countries. London itself is your biggest advantage — world-class museums, networking events, Erasmus dinners, theatre, markets. Build your social calendar from week one.',
      practicalTips: [
        'Get an Oyster card on day one — the Tube is your lifeline across the city',
        'Open a Monzo or Revolut account before arrival to avoid currency fees',
        'The university gym (Ethos) is excellent and free with your student card',
        'Visit the Career Hub in your first week — strong connections to London finance and consulting',
        'Book accommodation early (by March for a September start) — it fills up fast',
        'Pack layers — London weather changes every hour and that is part of the charm',
      ],
      message:
        "Go with an open mind and a full calendar. London rewards those who are curious and proactive. I came back with two internship offers, incredible friendships, and a completely different perspective on what a business career can look like. You will not regret it.",
    },
  },
  {
    id: 2,
    student: 'Matteo Conti',
    year: 'Fall 2024',
    program: 'Master in Management',
    university: 'Imperial College London',
    country: '🇬🇧 UK',
    tags: ['Finance focus', 'Big city energy', 'Top professors'],
    avatarColor: 'bg-sky-500',
    initials: 'MC',
    preview:
      'The professional exposure you get in London is unparalleled. Imperial connects you directly to the finance and tech industries in a way no classroom alone can. Prepare well and embrace every opportunity.',
    fullReport: {
      highlights: [
        "Imperial's brand opens doors — recruiters recognise it immediately across finance, consulting and tech",
        'The M&A course with a former Goldman MD is worth the exchange alone',
        'South Kensington is one of the most beautiful neighbourhoods in Europe — enjoy living there',
      ],
      courses: ['Mergers & Acquisitions', 'Design Thinking for Business', 'Applied Machine Learning for Finance', 'Marketing in the Digital Age'],
      courseTips:
        'M&A was exceptional — the professor brought real deal experience into every session. Design Thinking was surprisingly practical and creative. If you have any interest in fintech, the ML for Finance course is a fantastic opportunity to build new skills.',
      socialLife:
        'Join the Imperial College Investment Society from day one — it is the best recruiting and networking ground on campus. The neighbourhood is beautiful for evening walks and the South Bank on a sunny afternoon is unmissable. Students are ambitious and focused, which pushes you to be your best.',
      practicalTips: [
        'Join the Investment Society immediately — top recruiting ground for finance roles',
        'The library is open 24/7 and is an incredibly productive environment',
        'Take the Heathrow Express when you arrive — worth it with luggage after a long flight',
        'Use the alumni platform (Imperial Connect) — people respond quickly and are genuinely generous',
        'Explore a different London neighbourhood every weekend — each one feels like a different city',
        'Cook at home sometimes and save your budget for experiences — concerts, day trips, theatre',
      ],
      message:
        "Imperial is the kind of place that accelerates you. The academic environment is stimulating, the city is electric, and the people you meet will become part of your professional network for years. Commit fully and you will leave a different person.",
    },
  },
  {
    id: 3,
    student: 'Sophie Andersson',
    year: 'Spring 2024',
    program: 'MBA',
    university: 'Imperial College London',
    country: '🇬🇧 UK',
    tags: ['MBA community', 'Alumni network', 'Multicultural'],
    avatarColor: 'bg-emerald-500',
    initials: 'SA',
    preview:
      "One of the best decisions of my career. The MBA cohort at Imperial is diverse, ambitious, and deeply supportive. The alumni network alone is worth the semester — people are generous with their time and connections.",
    fullReport: {
      highlights: [
        'Guest lecturers from Permira, KKR and Advent International in the Private Equity course',
        'The MBA community organises treks, case competitions and socials that bond you instantly',
        "Imperial's alumni network is global and genuinely active — mentors respond within days",
      ],
      courses: ['Private Equity & Venture Capital', 'Behavioural Finance', 'Leadership in Complex Organisations', 'Digital Transformation Strategy'],
      courseTips:
        'Private Equity was outstanding — you will leave with a real understanding of how deals work and a network of practitioners. Behavioural Finance will change how you think about every decision you make. Leadership brought together the whole cohort in a way no other course did.',
      socialLife:
        'The MBA community is tight-knit and incredibly supportive. Weekly pub nights, weekend treks to Edinburgh, Oxford and the Cotswolds. The Women in Business club is particularly active and welcoming. You will feel part of a real community from the very first week.',
      practicalTips: [
        'Register for the Imperial MBA mentorship programme — mentors are C-suite executives who actually care',
        'Attend both careers fairs — major firms recruit directly on campus',
        'Get a National Rail card for cheaper weekend travel across the UK',
        'Walk along the Thames on your first weekend — it helps you feel at home in the city',
        'Say yes to everything in the first month — that is how you build your cohort friendships',
        'Use the university counselling and career services — both are excellent and free',
      ],
      message:
        "If Imperial is on your list, take it without hesitation. You will leave with a network that spans continents, a perspective that is truly global, and memories that last a lifetime. Go with an open mind — the experience will shape you in ways you cannot predict.",
    },
  },
  {
    id: 4,
    student: 'Jonas Weber',
    year: 'Fall 2023',
    program: 'Master in Management',
    university: 'Imperial College London',
    country: '🇬🇧 UK',
    tags: ['Personal growth', 'Startup scene', 'Immersive'],
    avatarColor: 'bg-amber-500',
    initials: 'JW',
    preview:
      'A truly immersive experience that pushed me to grow in ways I did not expect. Be proactive, join communities early, and London will reward you with incredible people, ideas, and opportunities.',
    fullReport: {
      highlights: [
        'The Imperial Hub startup space is open to all students — a fantastic creative and entrepreneurial environment',
        'Wilson House on-campus residence builds friendships instantly through its strong community feel',
        'London gives you access to some of the best free cultural experiences in the world',
      ],
      courses: ['Financial Accounting for Managers', 'Operations and Supply Chain', 'Consumer Psychology', 'International Business Law'],
      courseTips:
        'Operations and Supply Chain was unexpectedly fascinating — very hands-on and practical. Accounting was well structured and great for building fundamentals. Consumer Psychology offers a fresh perspective that is useful across any career path. Go to every optional session — the extra content is often the most valuable.',
      socialLife:
        'Join societies early — the Debate Society and the Startup Hub transformed my experience. Once you find your community, London becomes a playground. There is always something happening: art openings, networking events, free concerts, food markets. The city rewards curiosity.',
      practicalTips: [
        'Join at least two societies in your first week — they are the best way to find your people',
        'The Imperial Hub is open to all students and a great place to work, think and connect',
        'Budget for at least three UK weekend trips — Bath, Oxford and Brighton are all worth it',
        'Pre-read course materials before term starts to hit the ground running',
        'Explore a new food market every weekend — London has the best food scene in Europe',
        'Walk everywhere when the weather allows — you discover the city one street at a time',
      ],
      message:
        "Be proactive from day one. The more you put in, the more London and Imperial give back. Join things, talk to people, explore the city, say yes. By the end you will look back and realise it was one of the defining experiences of your life.",
    },
  },
];

/* ─── Download report ────────────────────────────────── */

function generateReportText(exp) {
  const { student, year, program, university, country, fullReport: r } = exp;
  const lines = [
    '═══════════════════════════════════════════════════════════════',
    '         STUDENT EXCHANGE EXPERIENCE REPORT',
    '         Unixplore — ESMT Berlin Student Portal',
    '═══════════════════════════════════════════════════════════════',
    '',
    `Student:           ${student}`,
    `Program:           ${program}`,
    `Exchange Period:   ${year}`,
    `Host University:   ${university} (${country.replace(/[^\x00-\x7F]/g, '').trim()})`,
    '',
    '───────────────────────────────────────────────────────────────',
    '  TOP HIGHLIGHTS',
    '───────────────────────────────────────────────────────────────',
    '',
    ...r.highlights.map((h, i) => `  ${i + 1}. ${h}`),
    '',
    '───────────────────────────────────────────────────────────────',
    '  RECOMMENDED COURSES',
    '───────────────────────────────────────────────────────────────',
    '',
    ...r.courses.map((c, i) => `  ${i + 1}. ${c}`),
    '',
    r.courseTips,
    '',
    '───────────────────────────────────────────────────────────────',
    '  SOCIAL LIFE & THE CITY',
    '───────────────────────────────────────────────────────────────',
    '',
    r.socialLife,
    '',
    '───────────────────────────────────────────────────────────────',
    '  PRACTICAL TIPS',
    '───────────────────────────────────────────────────────────────',
    '',
    ...r.practicalTips.map((t, i) => `  ${i + 1}. ${t}`),
    '',
    '───────────────────────────────────────────────────────────────',
    '  A MESSAGE TO FUTURE EXCHANGE STUDENTS',
    '───────────────────────────────────────────────────────────────',
    '',
    `  "${r.message}"`,
    '',
    '═══════════════════════════════════════════════════════════════',
    '  Report generated via Unixplore Student Portal',
    '  © ESMT Berlin — International Office',
    '═══════════════════════════════════════════════════════════════',
  ];
  return lines.join('\n');
}

function downloadReport(exp) {
  const text = generateReportText(exp);
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const safeName = exp.student.toLowerCase().replace(/\s+/g, '_');
  a.download = `experience_${safeName}_${exp.year.replace(/\s+/g, '_').toLowerCase()}.txt`;
  a.click();
  URL.revokeObjectURL(url);
}

/* ─── Experience card ────────────────────────────────── */

function ExperienceCard({ exp, onOpen }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 hover:border-indigo-200 transition-colors flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-10 h-10 rounded-full ${exp.avatarColor} flex items-center justify-center text-white text-sm font-semibold flex-shrink-0`}>
          {exp.initials}
        </div>
        <div>
          <p className="text-sm font-semibold text-[#0b1c30]">{exp.student}</p>
          <p className="text-xs text-slate-400 mt-0.5">{exp.program} · {exp.year}</p>
        </div>
        <span className="ml-auto flex items-center gap-1 text-xs font-semibold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full">
          <span className="material-symbols-outlined text-[13px]">thumb_up</span>
          Recommends
        </span>
      </div>

      {/* Tags */}
      <div className="flex flex-wrap gap-1.5 mb-4">
        {exp.tags.map(tag => (
          <span key={tag} className="text-xs font-medium bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded-full">
            {tag}
          </span>
        ))}
      </div>

      {/* Highlights preview */}
      <div className="bg-slate-50 rounded-xl p-3.5 mb-4 space-y-2">
        {exp.fullReport.highlights.slice(0, 2).map((h, i) => (
          <div key={i} className="flex items-start gap-2">
            <span className="material-symbols-outlined text-indigo-500 text-[15px] mt-0.5 flex-shrink-0"
              style={{ fontVariationSettings: "'FILL' 1" }}>
              star
            </span>
            <p className="text-xs text-[#464555] leading-relaxed">{h}</p>
          </div>
        ))}
      </div>

      {/* Preview quote */}
      <p className="text-sm text-[#464555] leading-relaxed italic flex-1 mb-5">
        "{exp.preview}"
      </p>

      {/* Actions */}
      <div className="flex items-center gap-2 pt-4 border-t border-slate-50">
        <button
          onClick={() => onOpen(exp)}
          className="flex-1 py-2 rounded-xl border border-indigo-200 text-indigo-600 text-xs font-medium hover:bg-indigo-50 transition-colors flex items-center justify-center gap-1.5"
        >
          <span className="material-symbols-outlined text-[14px]">open_in_full</span>
          Read Full Report
        </button>
        <button
          onClick={() => downloadReport(exp)}
          className="flex-1 py-2 rounded-xl bg-[#4343d5] text-white text-xs font-medium hover:bg-indigo-700 transition-colors flex items-center justify-center gap-1.5"
        >
          <span className="material-symbols-outlined text-[14px]">download</span>
          Download
        </button>
      </div>
    </div>
  );
}

/* ─── Full report modal ──────────────────────────────── */

function ReportModal({ exp, onClose }) {
  if (!exp) return null;
  const r = exp.fullReport;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(11,28,48,0.55)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Modal header */}
        <div className="sticky top-0 bg-white px-6 pt-6 pb-4 border-b border-slate-100 flex items-start justify-between z-10">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full ${exp.avatarColor} flex items-center justify-center text-white text-sm font-semibold`}>
              {exp.initials}
            </div>
            <div>
              <p className="font-display text-base font-semibold text-[#0b1c30]">{exp.student}</p>
              <p className="text-xs text-slate-400 mt-0.5">{exp.program} · {exp.year} · {exp.university}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center text-slate-400 hover:bg-slate-50 hover:text-slate-600 transition-colors flex-shrink-0"
          >
            <span className="material-symbols-outlined text-[18px]">close</span>
          </button>
        </div>

        {/* Modal body */}
        <div className="px-6 py-5 space-y-6">

          {/* Highlights */}
          <section>
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-1.5">
              <span className="material-symbols-outlined text-[14px] text-indigo-500">auto_awesome</span>
              Top Highlights
            </h3>
            <div className="space-y-2.5">
              {r.highlights.map((h, i) => (
                <div key={i} className="flex items-start gap-3 bg-indigo-50 rounded-xl px-4 py-3">
                  <span className="material-symbols-outlined text-indigo-500 text-[16px] mt-0.5 flex-shrink-0"
                    style={{ fontVariationSettings: "'FILL' 1" }}>
                    star
                  </span>
                  <p className="text-sm text-indigo-800 leading-relaxed">{h}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Courses */}
          <section>
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-1.5">
              <span className="material-symbols-outlined text-[14px] text-indigo-500">menu_book</span>
              Recommended Courses
            </h3>
            <div className="flex flex-wrap gap-2 mb-3">
              {r.courses.map(c => (
                <span key={c} className="text-xs font-medium bg-slate-100 text-slate-600 px-2.5 py-1 rounded-lg">
                  {c}
                </span>
              ))}
            </div>
            <p className="text-sm text-[#464555] leading-relaxed">{r.courseTips}</p>
          </section>

          {/* Social life */}
          <section>
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-1.5">
              <span className="material-symbols-outlined text-[14px] text-indigo-500">groups</span>
              Social Life & The City
            </h3>
            <p className="text-sm text-[#464555] leading-relaxed">{r.socialLife}</p>
          </section>

          {/* Practical tips */}
          <section>
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-1.5">
              <span className="material-symbols-outlined text-[14px] text-indigo-500">tips_and_updates</span>
              Practical Tips
            </h3>
            <ul className="space-y-2.5">
              {r.practicalTips.map((tip, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <span className="w-5 h-5 rounded-full bg-indigo-50 text-indigo-600 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                    {i + 1}
                  </span>
                  <p className="text-sm text-[#464555] leading-relaxed">{tip}</p>
                </li>
              ))}
            </ul>
          </section>

          {/* Message */}
          <section className="bg-indigo-50 rounded-xl p-5 border border-indigo-100">
            <h3 className="text-xs font-bold text-indigo-600 uppercase tracking-widest mb-3 flex items-center gap-1.5">
              <span className="material-symbols-outlined text-[14px]">mail</span>
              A Message to Future Exchange Students
            </h3>
            <p className="text-sm text-indigo-800 leading-relaxed italic">"{r.message}"</p>
          </section>
        </div>

        {/* Modal footer */}
        <div className="sticky bottom-0 bg-white px-6 py-4 border-t border-slate-100 flex gap-3">
          <button
            onClick={() => downloadReport(exp)}
            className="flex-1 py-2.5 bg-[#4343d5] text-white rounded-xl text-sm font-medium hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2"
          >
            <span className="material-symbols-outlined text-[16px]">download</span>
            Download Report (.txt)
          </button>
          <button
            onClick={onClose}
            className="px-5 py-2.5 border border-slate-200 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Main screen ────────────────────────────────────── */

export default function StudentExperiences() {
  return (
    <StudentLayout>
      <WorkInProgress
        icon="menu_book"
        title="Student Experiences"
        description="Read first-hand reports from students who previously exchanged at your destination university."
      />
    </StudentLayout>
  );

  // ── Real page below — remove the return above to restore it ──
  // eslint-disable-next-line no-unreachable
  const [openExp, setOpenExp] = useState(null);
  const [search, setSearch]   = useState('');

  const filtered = experiences.filter(e =>
    e.student.toLowerCase().includes(search.toLowerCase()) ||
    e.tags.some(t => t.toLowerCase().includes(search.toLowerCase())) ||
    e.year.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <StudentLayout>
      {/* Page header */}
      <div className="mb-8">
        <h1 className="font-display text-3xl font-semibold text-[#0b1c30]">Student Experiences</h1>
        <p className="text-[#464555] mt-1">
          Reports and recommendations from ESMT students who exchanged at Imperial College London
        </p>
      </div>

      {/* Summary banner */}
      <div className="bg-[#4343d5] rounded-2xl p-6 mb-8 flex items-center gap-6">
        <div className="w-14 h-14 rounded-2xl bg-white/15 flex items-center justify-center flex-shrink-0">
          <span className="material-symbols-outlined text-white text-[28px]">school</span>
        </div>
        <div className="flex-1">
          <p className="text-white font-display text-lg font-semibold">Imperial College London</p>
          <p className="text-indigo-200 text-sm mt-0.5">London, United Kingdom · Bilateral Partner</p>
        </div>
        <div className="flex items-center gap-8 flex-shrink-0">
          <div className="text-center">
            <p className="font-display text-3xl font-bold text-white">{experiences.length}</p>
            <p className="text-indigo-200 text-xs mt-0.5">Reports</p>
          </div>
          <div className="text-center">
            <p className="font-display text-3xl font-bold text-white">3</p>
            <p className="text-indigo-200 text-xs mt-0.5">Years of data</p>
          </div>
          <div className="text-center">
            <p className="font-display text-3xl font-bold text-emerald-400">100%</p>
            <p className="text-indigo-200 text-xs mt-0.5">Recommend</p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="flex items-center gap-3 mb-6">
        <div className="relative flex-1 max-w-sm">
          <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[18px]">search</span>
          <input
            type="text"
            placeholder="Search by name, tag or year..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200 text-slate-700 placeholder-slate-400"
          />
        </div>
        <span className="text-sm text-slate-400">{filtered.length} report{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Cards grid */}
      {filtered.length > 0 ? (
        <div className="grid grid-cols-2 gap-6">
          {filtered.map(exp => (
            <ExperienceCard key={exp.id} exp={exp} onOpen={setOpenExp} />
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-100 p-12 text-center">
          <span className="material-symbols-outlined text-slate-300 text-[48px]">search_off</span>
          <p className="text-slate-400 text-sm mt-3">No reports match your search.</p>
        </div>
      )}

      {/* Info note */}
      <div className="mt-6 flex items-start gap-3 bg-slate-50 border border-slate-100 rounded-2xl p-4">
        <span className="material-symbols-outlined text-slate-400 text-[20px] flex-shrink-0 mt-0.5">info</span>
        <p className="text-xs text-slate-500 leading-relaxed">
          These reports are written by ESMT Berlin students after completing their exchange semester at Imperial College London.
          They share recommendations, tips and personal insights to help you make the most of your experience. Download any report to save it offline.
        </p>
      </div>

      {/* Modal */}
      <ReportModal exp={openExp} onClose={() => setOpenExp(null)} />
    </StudentLayout>
  );
}
